import { Link } from 'react-router-dom'
import { ArrowRight, Check, ShieldCheck, Sparkles, Target } from 'lucide-react'

import { brandifyNode } from '@/components/brand/kindlumen'
import { SeoHead } from '@/components/seo/seo-head'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { SectionHeading } from '@/components/ui/section-heading'
import { createMetadata, marketingSeoPages } from '@/lib/seo'

const missionCards = [
  {
    icon: Target,
    title: 'Make fundraising simpler',
    description: 'Launch quality donation flows without setup overhead.',
  },
  {
    icon: Sparkles,
    title: 'Respect the donor experience',
    description: 'Giving should feel fast and trustworthy end-to-end.',
  },
  {
    icon: ShieldCheck,
    title: 'Reduce operational drag',
    description: 'Cleaner tools, less maintenance behind every campaign.',
  },
] as const

const originPoints = [
  {
    title: 'Patched-together tools',
    description: 'Plugins, workarounds, and disconnected reporting create more friction than they remove.',
  },
  {
    title: 'Sales-heavy platforms',
    description: 'A nonprofit shouldn’t need a long buying cycle to launch better donations.',
  },
  {
    title: 'Pricing should be obvious',
    description: 'Transparent infrastructure, not hidden costs.',
  },
] as const

const visionPoints = [
  'The easiest Stripe donation form for nonprofit websites',
  'Transparent pricing from day one',
  'From one donation form to multiple campaigns',
] as const

const principles = [
  {
    title: 'Simplicity over complexity',
    description: 'If a workflow is hard to explain, it’s too hard to run.',
  },
  {
    title: 'Transparent pricing',
    description: 'Know what you pay, when you pay it, and why.',
  },
  {
    title: 'Nonprofit-first',
    description: 'Designed for how fundraising teams actually operate.',
  },
  {
    title: 'Real outcomes',
    description: 'A maintainable giving flow over feature sprawl.',
  },
] as const

const metadata = createMetadata(marketingSeoPages.about)

export default function AboutPage() {
  return (
    <div className="space-y-20 pb-20 pt-10 sm:space-y-24">
      <SeoHead metadata={metadata} />
      <section className="marketing-shell">
        <div className="grid gap-8 xl:grid-cols-[1.02fr_0.98fr] xl:items-start">
          <div className="space-y-8 py-4 lg:pr-8">
            <div className="space-y-5">
              <div className="eyebrow">About KindLumen</div>
              <div className="space-y-4">
                <h1 className="max-w-3xl text-balance text-[2.45rem] font-semibold leading-[1.1] sm:text-[3.35rem] sm:leading-[1.06]">
                  Embed a Stripe donation form on the website you already have.
                </h1>
                <p className="max-w-2xl text-base leading-7 text-ink-soft sm:text-lg sm:leading-8">
                  A free Stripe donation form for the website you already have — without plugin sprawl or enterprise drag.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/features">
                  View features
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="secondary">
                <Link to="/signup">Start fundraising</Link>
              </Button>
            </div>
          </div>

          <div className="panel-dark overflow-hidden p-8 lg:p-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                Why teams choose KindLumen
              </div>
              <h2 className="max-w-lg text-3xl font-semibold leading-[1.18] text-white sm:text-[2.25rem]">
                Clear, modern, usable from day one.
              </h2>
            </div>

            <div className="mt-8 grid gap-4">
              {[
                'Works on WordPress and non-WordPress websites',
                'Donations go straight to your own Stripe — KindLumen never holds your funds',
                'No need to replace your website or maintain a custom payment integration',
              ].map((item) => (
                <div key={item} className="rounded-[24px] border border-white/10 bg-white/10 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15">
                      <Check className="h-3.5 w-3.5 text-white" />
                    </div>
                    <p className="text-sm leading-6 text-white/75">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <SectionHeading eyebrow="Our mission" title="Easier to launch. Easier to trust. Easier to grow." />

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {missionCards.map((item) => (
            <Card key={item.title} className="h-full">
              <div className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-brand/10 text-brand">
                <item.icon className="h-5 w-5" />
              </div>
              <CardTitle className="mt-5 text-xl">{item.title}</CardTitle>
              <CardDescription className="mt-4">{item.description}</CardDescription>
            </Card>
          ))}
        </div>
      </section>

      <section className="marketing-shell">
        <div className="grid gap-6 xl:grid-cols-[1.02fr_0.98fr]">
          <div className="panel p-8 lg:p-10">
            <div className="space-y-4">
              <div className="eyebrow">Why we started</div>
              <h2 className="max-w-2xl text-3xl font-semibold leading-[1.18] sm:text-[2.25rem]">
                Fundraising software got too complicated.
              </h2>
              <p className="max-w-2xl text-base leading-7 text-ink-soft sm:text-lg sm:leading-8">
                Nonprofits were stuck between clunky plugins and platforms that demanded sales calls before they even helped.
              </p>
            </div>

            <div className="mt-8 grid gap-4">
              {originPoints.map((item) => (
                <div key={item.title} className="rounded-[24px] border border-line bg-surface-strong px-5 py-5">
                  <p className="text-lg font-semibold leading-[1.24] text-ink">{item.title}</p>
                  <p className="mt-3 text-sm leading-6 text-ink-soft">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="panel-dark overflow-hidden p-8 lg:p-10">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                Our vision
              </div>
              <h2 className="max-w-lg text-3xl font-semibold leading-[1.18] text-white sm:text-[2.25rem]">
                A focused donation platform teams can grow with.
              </h2>
            </div>

            <div className="mt-8 grid gap-4">
              {visionPoints.map((item) => (
                <div key={item} className="rounded-[24px] border border-white/10 bg-white/10 px-5 py-4">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/15">
                      <Check className="h-3.5 w-3.5 text-white" />
                    </div>
                    <p className="text-sm leading-6 text-white/75">{brandifyNode(item, 'inverse')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-shell">
        <SectionHeading eyebrow="Our principles" title="What shapes every decision." />

        <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {principles.map((item) => (
            <Card key={item.title} className="h-full">
              <CardTitle className="text-xl">{item.title}</CardTitle>
              <CardDescription className="mt-4">{item.description}</CardDescription>
            </Card>
          ))}
        </div>
      </section>

      <section className="marketing-shell">
        <div className="panel-dark overflow-hidden p-8 lg:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.8fr] lg:items-end">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80">
                Start today
              </div>
              <h2 className="max-w-2xl text-4xl font-semibold leading-[1.18] text-white sm:text-[2.8rem]">
                Start fundraising today.
              </h2>
            </div>

            <div className="flex flex-wrap gap-3 lg:justify-end">
              <Button asChild size="lg">
                <Link to="/signup">Sign up</Link>
              </Button>
              <Button asChild size="lg" variant="dark">
                <Link to="/pricing">View pricing</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export const Component = AboutPage
